import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  AlertTriangle,
  FileSpreadsheet,
  Loader2,
  FileText,
  MapPin,
  Building,
  ChevronLeft,
  ChevronRight,
  Info,
  Truck,
  Package,
  Banknote,
  ShoppingBag,
  AlertCircle,
} from 'lucide-react';
import { HelpTooltip } from '../../components/ui/help-tooltip';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (rate) => {
  return `${((rate || 0) * 100).toFixed(2)}%`;
};

// Help text definitions for tooltips
const HELP_TEXT = {
  // Summary cards
  paidInvoices: "Number of invoices with status PAID during this period. Only paid invoices are included in cash-basis reporting.",
  totalSales: "Grand total of all paid invoices (subtotal + shipping + tax). This is the full amount collected from customers.",
  productSubtotal: "Sum of product/service line items before shipping and tax. This is your gross product revenue.",
  shipping: "Total shipping charges collected. Tracked separately from product sales.",
  taxCollected: "Total sales tax actually charged on invoices and collected from customers.",
  taxOwed: "Tax liability to CDTFA. Calculated as: (CA taxable sales × default rate). Only CA destination sales generate tax owed.",
  overCollected: "Tax Collected minus Tax Owed. Positive = over-collected (you owe less than collected, liability to refund or credit). Negative = under-collected.",
  defaultRate: "The CA sales tax rate used to calculate tax owed. Applied to CA PICKUP and CA SHIP orders.",
  nonTaxableSales: "Sum of taxable amounts (subtotal - discount) for invoices NOT subject to CA tax. Includes: Non-CA shipments + Tax-exempt customers. Does NOT include shipping or the tax that was collected.",
  caTaxableSales: "Taxable amount (subtotal - discount) for CA-destination sales. Includes PICKUP orders (business nexus) and SHIP to CA addresses. This is your CDTFA tax base.",
  nonCaTaxableSales: "Taxable amount for out-of-state shipments. These are NOT subject to CA tax, but may have had CA tax incorrectly collected (over-collection).",
  // Invoice table columns
  subtotal: "Product/service line items total before discount, shipping, and tax.",
  shippingCol: "Shipping charges for this invoice.",
  taxable: "Tax base amount (subtotal - discount). This is what tax is calculated on.",
  collected: "Sales tax actually charged on this invoice.",
  total: "Grand total charged to customer (taxable + tax + shipping).",
  owed: "Tax owed to CDTFA for this invoice. CA destinations = taxable × rate, Non-CA = $0.",
  overCollectedCol: "Collected minus Owed. Positive = over-collected for this invoice.",
};

// Status badge
const getStatusBadge = (status) => {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>;
    case 'final':
      return <Badge variant="default">Final</Badge>;
    case 'filed':
      return <Badge className="bg-green-600">Filed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

// Fulfillment badge
const getFulfillmentBadge = (type) => {
  if (type === 'PICKUP') {
    return <Badge variant="outline" className="text-xs gap-1"><Package className="h-3 w-3" />Pickup</Badge>;
  }
  return <Badge variant="outline" className="text-xs gap-1"><Truck className="h-3 w-3" />Ship</Badge>;
};

export default function SalesTaxReportDetail({ reportId: propReportId, onBack, onNavigateToOrder }) {
  const reportId = propReportId;
  
  const [report, setReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showFiledModal, setShowFiledModal] = useState(false);
  const [filing, setFiling] = useState(false);
  const [filingData, setFilingData] = useState({ payment_reference: '', filing_notes: '' });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const limit = 20;
  
  // Filters
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');
  const [shipStateFilter, setShipStateFilter] = useState('all');

  // Navigate back handler
  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Navigate to order handler
  const handleNavigateToOrder = (orderId) => {
    if (onNavigateToOrder) {
      onNavigateToOrder(orderId);
    }
  };

  // Fetch report
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/tax/ca/reports/${reportId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Report not found');
      const data = await response.json();
      setReport(data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
      handleBack();
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setLoadingInvoices(true);
      let url = `${API_BASE}/api/tax/ca/reports/${reportId}/invoices?page=${page}&limit=${limit}`;
      if (exceptionsOnly) {
        url += '&exceptions_only=true';
      }
      if (fulfillmentFilter && fulfillmentFilter !== 'all') {
        url += `&fulfillment_type=${fulfillmentFilter}`;
      }
      if (shipStateFilter && shipStateFilter !== 'all') {
        url += `&ship_state=${shipStateFilter}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch invoices');
      const data = await response.json();
      setInvoices(data.items || []);
      setTotalInvoices(data.total || 0);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setLoadingInvoices(false);
    }
  }, [reportId, page, exceptionsOnly, fulfillmentFilter, shipStateFilter]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (report) {
      fetchInvoices();
    }
  }, [report, fetchInvoices]);

  // Mark as filed
  const handleMarkFiled = async () => {
    try {
      setFiling(true);
      const response = await fetch(`${API_BASE}/api/tax/ca/reports/${reportId}/mark-filed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(filingData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to mark as filed');
      }

      toast.success('Report marked as filed');
      setShowFiledModal(false);
      fetchReport();
    } catch (error) {
      console.error('Error marking filed:', error);
      toast.error(error.message);
    } finally {
      setFiling(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalInvoices / limit);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Report not found</p>
        <Button variant="link" onClick={handleBack}>
          Back to Reports
        </Button>
      </div>
    );
  }

  const { totals, exceptions, default_sales_tax_snapshot: taxSnapshot } = report;

  return (
    <div className="space-y-6" data-testid="tax-report-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">CA Sales Tax Report</h1>
              {getStatusBadge(report.status)}
              <Badge variant="outline" className="text-xs">{report.basis || 'cash'}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Period: <span className="font-medium">{report.period?.label}</span>
              {' • '}
              {report.period?.start_date} to {report.period?.end_date}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`${API_BASE}/api/tax/ca/reports/${reportId}/export/pdf`, '_blank')}
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`${API_BASE}/api/tax/ca/reports/${reportId}/export/summary`, '_blank')}
          >
            <Download className="h-4 w-4 mr-2" />
            Summary CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`${API_BASE}/api/tax/ca/reports/${reportId}/export/detail`, '_blank')}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Detail CSV
          </Button>
          {report.status !== 'filed' && (
            <Button onClick={() => setShowFiledModal(true)} data-testid="mark-filed-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark as Filed
            </Button>
          )}
        </div>
      </div>

      {/* Cash Basis Rationale Banner */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="py-3">
          <div className="flex items-start gap-2 text-sm">
            <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-amber-800">
              <strong>Cash Basis:</strong> Only PAID invoices are included. Tax owed is recomputed at report time based on 
              fulfillment type (PICKUP = CA rate) and ship-to state (CA = default rate, non-CA = 0%).
              Invoice tax amounts are not modified.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards - Row 1: Sales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Paid Invoices
              <HelpTooltip text={HELP_TEXT.paidInvoices} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{totals?.invoice_count || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <ShoppingBag className="h-4 w-4" />
              Total Sales
              <HelpTooltip text={HELP_TEXT.totalSales} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totals?.total_sales_paid)}</p>
            <p className="text-xs text-muted-foreground mt-1">Grand total of paid invoices</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Product/Subtotal
              <HelpTooltip text={HELP_TEXT.productSubtotal} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold">{formatCurrency(totals?.total_subtotal_paid)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Truck className="h-4 w-4" />
              Shipping
              <HelpTooltip text={HELP_TEXT.shipping} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold">{formatCurrency(totals?.total_shipping_paid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards - Row 2: Tax */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Tax Collected
              <HelpTooltip text={HELP_TEXT.taxCollected} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totals?.tax_collected_paid)}</p>
            <p className="text-xs text-muted-foreground">From paid invoices</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Tax Owed
              <HelpTooltip text={HELP_TEXT.taxOwed} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(totals?.tax_owed_paid)}</p>
            <p className="text-xs text-muted-foreground">Recomputed for CDTFA</p>
          </CardContent>
        </Card>
        <Card className={totals?.over_collected_paid > 0 ? 'border-green-200 bg-green-50/50' : totals?.over_collected_paid < 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Over-Collected
              <HelpTooltip text={HELP_TEXT.overCollected} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${totals?.over_collected_paid > 0 ? 'text-green-600' : totals?.over_collected_paid < 0 ? 'text-red-600' : ''}`}>
              {formatCurrency(totals?.over_collected_paid)}
            </p>
            <p className="text-xs text-muted-foreground">
              {totals?.over_collected_paid > 0 ? 'Liability' : totals?.over_collected_paid < 0 ? 'Under-collected' : 'Balanced'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Banknote className="h-4 w-4" />
              Default Rate
              <HelpTooltip text={HELP_TEXT.defaultRate} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-blue-600">{formatPercent(taxSnapshot?.rate)}</p>
            <p className="text-xs text-muted-foreground">{taxSnapshot?.city}, {taxSnapshot?.state}</p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Non-Taxable Sales Card */}
        <Card className="border-gray-200 bg-gray-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Non-Taxable Sales
              <HelpTooltip text={HELP_TEXT.nonTaxableSales} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold">{formatCurrency(totals?.non_taxable_sales_paid)}</p>
            <p className="text-xs text-muted-foreground mt-1">Non-CA + Tax Exempt (taxable amount only)</p>
          </CardContent>
        </Card>

        {/* CA vs Non-CA Sales */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <Building className="h-4 w-4" />
              Taxable Sales Breakdown
              <HelpTooltip text="Shows how taxable amounts are split between CA-destination (subject to CA tax) and non-CA destination (not subject to CA tax). These are taxable amounts only (subtotal - discount), not including shipping." />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                CA Sales (incl. Pickup):
                <HelpTooltip text={HELP_TEXT.caTaxableSales} className="ml-0" />
              </span>
              <span className="font-medium">{formatCurrency(totals?.ca_taxable_sales_paid)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                Non-CA Sales:
                <HelpTooltip text={HELP_TEXT.nonCaTaxableSales} className="ml-0" />
              </span>
              <span className="font-medium">{formatCurrency(totals?.non_ca_taxable_sales_paid)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Fulfillment Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              Fulfillment Logic
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="flex items-center gap-2">
              <Package className="h-3 w-3 text-blue-600" />
              <span><strong>PICKUP:</strong> Default CA rate</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-3 w-3 text-green-600" />
              <span><strong>SHIP CA:</strong> Default CA rate</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-3 w-3 text-gray-400" />
              <span><strong>SHIP Non-CA:</strong> 0%</span>
            </div>
          </CardContent>
        </Card>

        {/* Exceptions */}
        <Card className={
          (exceptions?.missing_ship_state_count || 0) + 
          (exceptions?.missing_tax_snapshot_count || 0) + 
          (exceptions?.non_ca_tax_collected_count || 0) > 0 
            ? 'border-amber-200 bg-amber-50/50' 
            : 'border-green-200 bg-green-50/50'
        }>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Exceptions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing ship state:</span>
              <Badge variant={exceptions?.missing_ship_state_count > 0 ? 'destructive' : 'outline'}>
                {exceptions?.missing_ship_state_count || 0}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Missing tax snapshot:</span>
              <Badge variant={exceptions?.missing_tax_snapshot_count > 0 ? 'destructive' : 'outline'}>
                {exceptions?.missing_tax_snapshot_count || 0}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Non-CA tax collected:</span>
              <Badge variant={exceptions?.non_ca_tax_collected_count > 0 ? 'destructive' : 'outline'}>
                {exceptions?.non_ca_tax_collected_count || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filing Info (if filed) */}
      {report.status === 'filed' && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Filing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Filed At</p>
              <p className="font-medium">{report.filed_at ? new Date(report.filed_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment Reference</p>
              <p className="font-medium">{report.payment_reference || '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-muted-foreground">Notes</p>
              <p className="font-medium">{report.filing_notes || '-'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice Drill-Down */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>Paid Invoice Details</CardTitle>
              <CardDescription>
                {totalInvoices} paid invoice{totalInvoices !== 1 ? 's' : ''} in this report
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="exceptions-only"
                  checked={exceptionsOnly}
                  onCheckedChange={(checked) => {
                    setExceptionsOnly(checked);
                    setPage(1);
                  }}
                />
                <Label htmlFor="exceptions-only" className="text-sm cursor-pointer">
                  Exceptions only
                </Label>
              </div>
              <Select value={fulfillmentFilter} onValueChange={(v) => { setFulfillmentFilter(v); setPage(1); }}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Fulfillment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="SHIP">Ship</SelectItem>
                  <SelectItem value="PICKUP">Pickup</SelectItem>
                </SelectContent>
              </Select>
              <Select value={shipStateFilter} onValueChange={(v) => { setShipStateFilter(v); setPage(1); }}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Ship State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  <SelectItem value="CA">CA Only</SelectItem>
                  <SelectItem value="NON-CA">Non-CA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No paid invoices found matching filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Paid Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Fulfillment</TableHead>
                      <TableHead>Ship To</TableHead>
                      <TableHead className="text-right">
                        Subtotal
                        <HelpTooltip text={HELP_TEXT.subtotal} />
                      </TableHead>
                      <TableHead className="text-right">
                        Shipping
                        <HelpTooltip text={HELP_TEXT.shippingCol} />
                      </TableHead>
                      <TableHead className="text-right">
                        Taxable
                        <HelpTooltip text={HELP_TEXT.taxable} />
                      </TableHead>
                      <TableHead className="text-right">
                        Collected
                        <HelpTooltip text={HELP_TEXT.collected} />
                      </TableHead>
                      <TableHead className="text-right">
                        Total
                        <HelpTooltip text={HELP_TEXT.total} />
                      </TableHead>
                      <TableHead className="text-right">
                        Owed
                        <HelpTooltip text={HELP_TEXT.owed} />
                      </TableHead>
                      <TableHead className="text-right">
                        Over-Coll.
                        <HelpTooltip text={HELP_TEXT.overCollectedCol} />
                      </TableHead>
                      <TableHead>Exception</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow 
                        key={inv.invoice_number} 
                        className={inv.is_exception ? 'bg-amber-50/50' : inv.tax_exempt ? 'bg-gray-50/50' : ''}
                        data-testid={`invoice-row-${inv.invoice_number}`}
                      >
                        <TableCell className="font-mono text-sm">
                          <Button
                            variant="link"
                            className="p-0 h-auto"
                            onClick={() => handleNavigateToOrder(inv.order_id)}
                          >
                            {inv.invoice_number}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate">
                          {inv.customer_name || '-'}
                        </TableCell>
                        <TableCell>
                          {getFulfillmentBadge(inv.fulfillment_type)}
                        </TableCell>
                        <TableCell>
                          {inv.fulfillment_type === 'PICKUP' ? (
                            <span className="text-muted-foreground italic text-sm">N/A</span>
                          ) : inv.ship_to_state ? (
                            <span className={inv.ship_to_state === 'CA' ? 'text-blue-600 font-medium' : ''}>
                              {inv.ship_to_city ? `${inv.ship_to_city}, ` : ''}{inv.ship_to_state}
                            </span>
                          ) : (
                            <span className="text-muted-foreground italic">Unknown</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(inv.subtotal_amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(inv.shipping_amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {inv.tax_exempt ? (
                            <span className="text-muted-foreground italic">Exempt</span>
                          ) : (
                            formatCurrency(inv.taxable_amount)
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(inv.tax_collected)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm font-medium">
                          {formatCurrency(inv.total_amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(inv.tax_owed)}
                        </TableCell>
                        <TableCell className={`text-right font-mono text-sm ${inv.over_collected > 0 ? 'text-green-600' : inv.over_collected < 0 ? 'text-red-600' : ''}`}>
                          {formatCurrency(inv.over_collected)}
                        </TableCell>
                        <TableCell>
                          {inv.is_exception ? (
                            <Badge variant="outline" className="text-amber-600 text-xs whitespace-nowrap">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {inv.exception_reason?.replace('Missing ', '').replace('Tax collected for ', '').slice(0, 15)}
                            </Badge>
                          ) : (
                            <span className="text-green-600 text-xs">✓</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({totalInvoices} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={!canPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={!canNext}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Mark as Filed Modal */}
      <Dialog open={showFiledModal} onOpenChange={setShowFiledModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Report as Filed</DialogTitle>
            <DialogDescription>
              Confirm that this report has been filed with CDTFA. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="Check #, confirmation number, etc."
                value={filingData.payment_reference}
                onChange={(e) => setFilingData(d => ({ ...d, payment_reference: e.target.value }))}
                data-testid="payment-reference-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Filing Notes (optional)</Label>
              <Textarea
                placeholder="Any notes about this filing..."
                value={filingData.filing_notes}
                onChange={(e) => setFilingData(d => ({ ...d, filing_notes: e.target.value }))}
                rows={3}
                data-testid="filing-notes-input"
              />
            </div>

            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              <p className="font-medium mb-1">Summary (Cash Basis)</p>
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <span>Total Sales:</span>
                <span className="font-medium text-foreground">{formatCurrency(totals?.total_sales_paid)}</span>
                <span>Tax Collected:</span>
                <span className="font-medium text-foreground">{formatCurrency(totals?.tax_collected_paid)}</span>
                <span>Tax Owed:</span>
                <span className="font-medium text-foreground">{formatCurrency(totals?.tax_owed_paid)}</span>
                <span>Over-Collected:</span>
                <span className={`font-medium ${totals?.over_collected_paid > 0 ? 'text-green-600' : totals?.over_collected_paid < 0 ? 'text-red-600' : 'text-foreground'}`}>
                  {formatCurrency(totals?.over_collected_paid)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFiledModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkFiled} 
              disabled={filing}
              data-testid="confirm-filed-btn"
            >
              {filing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Filed
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
